import {
    createPromiseWithResolvers,
    ExtendedBitmapText,
    ExtendedContainer,
    ExtendedGraphics,
    ExtendedSprite,
    gsap,
    SkySpine,
    soundService
} from "@aceroll/core";
import { currency } from "@aceroll/core/core/services";
import {
    ANIMATION_DURATION, ANIMATIONS, ASSETS, BITMAP_FONT, CHARACTER_POSITION_Y,
    CHARACTERS, MAX_MULTIPLIER, MULTIPLIER_STAGES, PHASES, SKINS, SOUNDS, SPINE_EVENTS
} from './config';

export default class BigWinPopup extends ExtendedContainer {
    constructor() {
        super('BigWinPopup');

        this._initPopupState();
        this._setupPopup();
    }

    _initPopupState() {
        this._finalPhase = null
        this._currentWinAmount = 0;
        this._currentCharacter = null;
        this._currentPhase = PHASES.BIG;
        this._currentCounterTween = null;
        this._backgroundShowTween = null;

        this._inited = true;
    }
    _setupPopup() {
        this.bgContainer = this.addChild(new ExtendedContainer('bgContainer'));
        this.bgContainer.scaleMode = 'max';
        this._background = this.bgContainer.addChild(ExtendedSprite.fromAlias(ASSETS.BACKGROUND));
        this._background.anchor.set(0.5);
        this._background.alpha = 0;
        this._maskContainer = this._background.addChild(new ExtendedContainer('MaskContainer'))

        this.scaledContent = this.addChild(new ExtendedContainer('scaledContent'));
        this._character = this.scaledContent.addChild(new ExtendedContainer('character'));
        this._spine = this.scaledContent.addChild(new SkySpine(ASSETS.POPUP));

        this._addSpineEventsHandler();
        this._createCounterLabel();

        this.visible = false;

    }
    resize(width, height) {
        this.bgContainer.resize(width, height);
        this.scaledContent.resize(width, height);
    }

    async show(win, bet) {
        this._isSkipped = false;
        if (this._isHiding) {
            throw new Error(`Can't show BigWinPopup while previous popup didn't hide!`);
        }

        this._finalMultiplier = win / bet;
        if (this._finalMultiplier < MULTIPLIER_STAGES.BIG) {
            return Promise.resolve();
        }
        this._finalAmount = win;
        this._bet = bet;

        this._playPopupAnimations();

        this._popupPromiseWithResolvers = createPromiseWithResolvers();
        return this._popupPromiseWithResolvers.promise;
    }

    async _playPopupAnimations() {
        if (!this._inited) {
            this._initPopupState();
        }

        this._initializeAnimation();

        try {
            for (const phase of Object.values(PHASES)) {
                if (this._isSkipped) break;
                if (this._finalMultiplier >= MULTIPLIER_STAGES[phase]) {
                    this._currentPhase = phase;
                    await this._playPhaseSequence();
                }
            }

            if (!this._isSkipped) {
                await this._spine.setImmediately(ANIMATIONS[this._finalPhase].IDLE);
                await this.hide();
            }
        } catch (error) {
            if (error.message === 'SKIP') {
                await this.handleSkipSequence();
            }
        }
    }

    async handleSkipSequence() {
        if (this._isSkipped || !this._inited) {
            return;
        }
        this._isSkipped = true;
        this._forceShowBackground();
        this._updateCounter(this._finalAmount);
        this._showWinLabel(true);
        this._currentPhase = this._finalPhase;
        this._cleanUpCounterTween();
        this._stopSounds();
        await this._updateCurrentCharacter(true);
        this._updateSpineSkin();
        this._spine.stop();
        await this._spine.setImmediately(ANIMATIONS[this._finalPhase].IDLE);

        return this.hide();
    }

    _forceShowBackground() {
        if (this._backgroundShowTween) {
            this._backgroundShowTween.progress(100);
        } else {
            this._background.alpha = 1;
        }
    }

    _stopSounds() {
        Object.values(SOUNDS).forEach(sound => soundService.stop(sound));
    }

    _createCounterLabel() {
        this._winCounterLabel = new ExtendedBitmapText('', BITMAP_FONT.ALIAS, { align: BITMAP_FONT.ALIGN });
        this._winCounterLabel.fontSize = 8;
        this._spine.addObjectToSlot(this._winCounterLabel, 'number_holder_1');
        this._winCounterLabel.visible = false;
    }

    _initializeAnimation() {
        this.visible = true;
        this._currentWinAmount = 0;
        this._updateCounter(0);
        this._selectFinalPhase();

        this._spine.reset();
        this._spine.visible = true;

        this._currentPhase = PHASES.BIG;
    }

    _showBackGround() {
        this._backgroundShowTween = gsap.to(this._background, { alpha: 1, duration: 1.5 });
    }

    _hideBackGround() {
        const maskContainer = this._maskContainer;
        const leftMask = maskContainer.addChild(this._createMaskElement(960));
        const rightMask = maskContainer.addChild(this._createMaskElement(0));

        maskContainer.position.set(0, -960);
        this._background.mask = maskContainer;
        this._animateMask(leftMask, rightMask);
    }

    _createMaskElement(pivotX) {
        const mask = new ExtendedGraphics('mask');
        mask.pivot.set(960, 0);
        mask.beginFill(0x000000);
        mask.drawRect(pivotX, 0, 960, 1920);
        mask.endFill();

        return mask;
    }

    _animateMask(leftMask, rightMask) {
        gsap.to([leftMask, rightMask], {
            width: 0,
            duration: ANIMATION_DURATION.HIDE_POPUP,
            ease: "sine.in",
            onComplete: () => {
                this._background.mask = null;
                this._background.alpha = 0;
                leftMask.destroy();
                rightMask.destroy();
            }
        });
    }

    async _playPhaseSequence() {
        if (this._isSkipped) {
            throw new Error('SKIP');
        }

        if (this._currentPhase === PHASES.BIG) {
            await this._playBigWinPhaseSteps();
        } else {
            await this._spine.setImmediately(ANIMATIONS.TRANSITIONS[this._currentPhase]);
        }
        soundService.play(SOUNDS[this._currentPhase]);

        if (this._isSkipped) {
            throw new Error('SKIP');
        }

        this._animateCounter(this._counterTargetAmount, 2);

        await this._spine.setImmediately(ANIMATIONS[this._currentPhase].IDLE);
    }

    async _playBigWinPhaseSteps() {
        this._updateSpineSkin();
        this._showBackGround();
        await this._spine.setImmediately(ANIMATIONS.SHOW);
        if (!this._isSkipped) {
            this._showWinLabel(this._isSkipped);
        }
    }

    _addSpineEventsHandler() {
        this._spine.state.addListener({
            event: (entry, event) => {
                if (event.data.name === SPINE_EVENTS.CHANGE_SKIN) {
                    this._updateSpineSkin();
                    this._updateCurrentCharacter();
                }

                if (event.data.name === SPINE_EVENTS.SHOW_CHARACTER) {
                    this._updateCurrentCharacter();
                }
            }
        });
    }

    _updateSpineSkin() {
        this._spine.setSkin(SKINS[this._currentPhase]);
    }

    _showWinLabel(force = false) {
        const duration = force ? 0 : ANIMATION_DURATION.SHOW_WIN_LABEL;
        this._winCounterLabel.visible = true;
        gsap.fromTo(this._winCounterLabel,
            { alpha: 0 },
            {
                alpha: 1,
                duration
            }
        );
    }

    _hideWinLabel() {
        gsap.fromTo(this._winCounterLabel,
            { alpha: 1 },
            {
                alpha: 0,
                duration: 0.6,
                onComplete: () => {
                    this._winCounterLabel.visible = false;
                }
            }
        )
    }

    _calculateMultiplier(winAmount, bet) {
        return winAmount / bet;
    }

    _updateCounter(value) {
        this._currentWinAmount = value;
        const formatedValue = currency.toMoney(Math.floor(value));
        this._winCounterLabel.setText(formatedValue);
        this._winCounterLabel.pivot.set(this._winCounterLabel.width / 2, this._winCounterLabel.height / 2);
    }

    _animateCounter(targetAmount, duration, ease = "power1.in") {
        soundService.play(SOUNDS.COUNTER);
        this._cleanUpCounterTween();

        this._currentCounterTween = gsap.to(this, {
            _currentWinAmount: targetAmount,
            duration,
            ease,
            onUpdate: () => this._updateCounter(this._currentWinAmount),
            onComplete: () => soundService.stop(SOUNDS.COUNTER)
        });
    }

    async _updateCurrentCharacter(force = false) {
        if (this._currentCharacter !== null) {
            const duration = force ? 0 : ANIMATION_DURATION.HIDE_CHARACTER;
            await this._hideCharacter(duration);
            this._removeCharacterSpine();
        }

        this._currentCharacter = new SkySpine(CHARACTERS[this._currentPhase]);
        this._currentCharacter.y = CHARACTER_POSITION_Y[this._currentPhase];
        this._currentCharacter.setImmediately('idle', true);
        this._character.addChild(this._currentCharacter);
        this._currentCharacter.alpha = 1;
    }

    _removeCharacterSpine() {
        if (!this._currentCharacter) { return; }

        this._currentCharacter.alpha = 0;
        this._currentCharacter.stop();
        this._currentCharacter.destroy();
        this._character.removeChild(this._currentCharacter);
        this._currentCharacter = null;

    }

    get _counterTargetAmount() {
        const currentStage = MAX_MULTIPLIER[this._currentPhase];
        if (this._finalMultiplier >= currentStage) {
            return currentStage * this._bet;
        } else {
            return this._finalAmount;
        }
    }

    _selectFinalPhase() {
        if (!this._finalPhase) {
            for (const phase of Object.values(PHASES)) {
                if (this._finalMultiplier < MAX_MULTIPLIER[phase]) {
                    this._finalPhase = phase;
                    break;
                }
            }

            if (!this._finalPhase) {
                this._finalPhase = PHASES.SUPER_MEGA;
            }
        }
    }

    get _hideAnimationName() {
        return ANIMATIONS[this._finalPhase].HIDE;
    }

    async _hideCharacter(duration) {
        if (!this._currentCharacter) return;

        return new Promise(resolve => {
            gsap.to(
                this._currentCharacter,
                {
                    alpha: 0,
                    duration: duration,
                    ease: "expo.in",
                    onComplete: () => {
                        this._removeCharacterSpine();
                        resolve()
                    }
                });
        });
    }

    async hide() {
        this._isHiding = true;
        this._hideWinLabel();
        this._hideBackGround();
        this._hideCharacter(ANIMATION_DURATION.HIDE_POPUP);
        await this._spine.setImmediately(this._hideAnimationName);
        this._cleanUpAfterHide();

        this._popupPromiseWithResolvers.resolve(true);
    }

    _cleanUpAfterHide() {
        this.visible = false;
        this._inited = false;
        this._isHiding = false;

        this._spine.reset();
        this._spine.visible = false;

        this._stopSounds();
        this._cleanUpCounterTween();
    }

    _cleanUpCounterTween() {
        if (this._currentCounterTween) {
            this._currentCounterTween.kill();
        }

        if (this._backgroundShowTween) {
            this._backgroundShowTween.kill();
        }
    }
    onLandscape() {
        this._background.position.set(960, 540);
        this._spine.position.set(960, 540);
        this._character.position.set(960, 540);

    }
    onPortrait() {
        this._background.position.set(540, 960);
        this._spine.position.set(540, 960);
        this._character.position.set(540, 960);
    }
}